import { Avatar, Clickable, Col, Icon, Row, TabControl, Text } from 'components';
import { Api } from 'lib';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Header, ParticipationSummary, SearchUsers } from 'screens/Admin/components';
import EffectUsageSummary from './components/EffectUsageSummary';
import GoalRushAnalyticsDrawer from './components/GoalRushAnalyticsDrawer';
import GoalRushDailyCard from './components/GoalRushDailyCard';
// Inline GoalRush panel hidden now that the analytics open in their own drawer; kept for
// switch-back.
// import GoalRushStats from './components/GoalRushStats';
import ItemActivityDrawer from './components/ItemActivityDrawer';
import ModuleBreakdownTable from './components/ModuleBreakdownTable';
import ModuleItemsList from './components/ModuleItemsList';
import ReferralSection from './components/ReferralSection';
import { fmtDuration, fmtNumber, fmtRate } from './format';

const ITEM_PAGE_SIZE = 25;

const TABS = ['Modules', 'Referrals'];
const TAB_KEYS = ['modules', 'referrals'];

const MODULE_OPTIONS = [
  { label: 'All modules', value: '' },
  { label: 'FanCam', value: 'fancam' },
  { label: 'TIRI – Tag It', value: 'tagit' },
  { label: 'TIRI – Rate It', value: 'rateit' },
  { label: 'FanWave', value: 'fanwave' },
  { label: 'GoalRush', value: 'goalrush' },
  { label: 'Predictions', value: 'predictions' },
  { label: 'Quizzes', value: 'quizzes' },
  { label: 'Polls', value: 'polls' },
  { label: 'FanChat', value: 'fanchat' },
  { label: 'Rewards', value: 'rewards' },
  { label: 'GloryBoard', value: 'ptw' },
  { label: 'Game Stat', value: 'playerStats' }
];

const inputClass = 'h-10 px-3 rounded-lg border border-[#CBD5E1] text-[13px] outline-none';

const Insights = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabIdx = Math.max(0, TAB_KEYS.indexOf(searchParams.get('tab')));
  const view = TAB_KEYS[tabIdx];
  const [teamFilter, setTeamFilter] = useState(null);
  const [moduleFilter, setModuleFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState(null);
  const [itemPage, setItemPage] = useState(0);
  const [itemsData, setItemsData] = useState(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemSort, setItemSort] = useState('events');
  const [selectedItem, setSelectedItem] = useState(null);
  // Refreshing is tracked apart from `loading` so a manual refresh leaves the current figures on
  // screen and only spins the button. Reusing `loading` would swap the list back to its skeleton,
  // which reads as "the data went away" for what is usually a no-change refetch.
  const [itemsRefreshing, setItemsRefreshing] = useState(false);
  const [fanChat, setFanChat] = useState(null);
  const [fanChatLoading, setFanChatLoading] = useState(false);
  const [goalRush, setGoalRush] = useState(null);
  const [goalRushLoading, setGoalRushLoading] = useState(false);
  const [goalRushRefreshing, setGoalRushRefreshing] = useState(false);
  const [goalRushOpen, setGoalRushOpen] = useState(false);
  // Only the latest GoalRush request may land: a filter change mid-fetch would otherwise let the
  // slower, older response overwrite the newer one.
  const goalRushRequestRef = useRef(0);

  // Bucket totals (top strip) + the always-full module breakdown (table below) come from
  // the same admin endpoint — the module filter only narrows the totals, not the table,
  // so switching modules never hides the rest of the breakdown.
  const getSummary = useCallback(async () => {
    setLoading(true);

    const params = new URLSearchParams();
    if (teamFilter) params.set('teamId', teamFilter._id);
    if (moduleFilter) params.set('module', moduleFilter);
    if (fromDate) params.set('from', new Date(fromDate).toISOString());
    if (toDate) params.set('to', new Date(`${toDate}T23:59:59.999`).toISOString());
    const qs = params.toString();

    const res = await Api.get(`/admin/insights/summary${qs ? `?${qs}` : ''}`, null, {
      silent: true
    });

    setSummary(res?.data || null);
    setLoading(false);
  }, [teamFilter, moduleFilter, fromDate, toDate]);

  useEffect(() => {
    getSummary().then();
  }, [getSummary]);

  // FanChat 2.0 topic figures are per team, so they only show once a team is picked and FanChat
  // is the module being looked at.
  const showFanChat =
    view === 'modules' &&
    !!teamFilter &&
    (moduleFilter === 'fanchat' || selectedModule === 'fanchat');

  useEffect(() => {
    if (!showFanChat) {
      setFanChat(null);
      return;
    }

    let cancelled = false;
    setFanChatLoading(true);

    const params = new URLSearchParams();
    params.set('teamId', teamFilter._id);
    if (fromDate) params.set('from', new Date(fromDate).toISOString());
    if (toDate) params.set('to', new Date(`${toDate}T23:59:59.999`).toISOString());

    Api.get(`/admin/insights/fanchat?${params.toString()}`, null, { silent: true }).then((res) => {
      if (cancelled) return;

      setFanChat(res?.data || null);
      setFanChatLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [showFanChat, teamFilter, fromDate, toDate]);

  // GoalRush analytics (brief §7) open in their own drawer, for one team or, with no team
  // picked, the whole platform, from the kicks-per-day card's "View more".

  // `quiet` (the drawer's refresh) keeps the current figures on screen and only spins the button.
  const loadGoalRush = useCallback(
    async ({ quiet = false } = {}) => {
      const request = ++goalRushRequestRef.current;
      if (quiet) setGoalRushRefreshing(true);
      else setGoalRushLoading(true);

      const params = new URLSearchParams();
      if (teamFilter) params.set('teamId', teamFilter._id);
      if (fromDate) params.set('from', new Date(fromDate).toISOString());
      if (toDate) params.set('to', new Date(`${toDate}T23:59:59.999`).toISOString());
      params.set('tz', Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');

      const res = await Api.get(`/admin/insights/goalrush?${params.toString()}`, null, {
        silent: true
      });
      if (request !== goalRushRequestRef.current) return;

      setGoalRush(res?.data || null);
      setGoalRushLoading(false);
      setGoalRushRefreshing(false);
    },
    [teamFilter, fromDate, toDate]
  );

  // GoalRush kicks per day as a card: beside the AR filters card on the all-modules view, and
  // on its own when GoalRush is the module filter.
  const showGoalRushCard = view === 'modules' && (!moduleFilter || moduleFilter === 'goalrush');

  // Fetched while the drawer or the card is showing, and again whenever the filters change.
  // Opening the drawer from the card's "View more" reuses what the card already loaded.
  const goalRushWanted = goalRushOpen || showGoalRushCard;
  useEffect(() => {
    if (!goalRushWanted) {
      goalRushRequestRef.current += 1;
      setGoalRush(null);
      setGoalRushLoading(false);
      setGoalRushRefreshing(false);
      return;
    }
    loadGoalRush().then();
  }, [goalRushWanted, loadGoalRush]);

  const closeGoalRush = useCallback(() => setGoalRushOpen(false), []);

  // Changing the module filter invalidates whatever item list/drawer is open — it was fetched
  // for the previously selected module (or "all modules") and would otherwise keep showing that
  // module's items/events under the new filter's breakdown.
  useEffect(() => {
    setSelectedModule(null);
    setItemsData(null);
    setItemPage(0);
    setSelectedItem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleFilter]);

  const getItems = useCallback(
    async (module, page, { quiet = false } = {}) => {
      if (quiet) setItemsRefreshing(true);
      else setItemsLoading(true);

      const params = new URLSearchParams();
      params.set('module', module);
      params.set('limit', ITEM_PAGE_SIZE);
      params.set('offset', page * ITEM_PAGE_SIZE);
      if (teamFilter) params.set('teamId', teamFilter._id);
      if (fromDate) params.set('from', new Date(fromDate).toISOString());
      if (toDate) params.set('to', new Date(`${toDate}T23:59:59.999`).toISOString());
      if (itemSort !== 'events') params.set('sort', itemSort);

      const res = await Api.get(`/admin/insights/items?${params.toString()}`, null, {
        silent: true
      });

      setItemsData(res?.data || null);
      if (quiet) setItemsRefreshing(false);
      else setItemsLoading(false);
    },
    [teamFilter, fromDate, toDate, itemSort]
  );

  useEffect(() => {
    if (!selectedModule) return;
    getItems(selectedModule, itemPage).then();
  }, [selectedModule, itemPage, getItems]);

  // Refetches the current page of the open module, keeping page and filter — an admin watching
  // one module's items should not be bounced back to page 1 to see whether a count moved.
  const handleRefreshItems = useCallback(() => {
    if (!selectedModule) return;
    getItems(selectedModule, itemPage, { quiet: true });
  }, [getItems, selectedModule, itemPage]);

  const handleModuleClick = useCallback((module) => {
    setSelectedModule((prev) => (prev === module ? null : module));
    setItemPage(0);
    setItemsData(null);
  }, []);

  // A new sort reorders the whole list, so page N of the old one means nothing.
  const handleItemSortChange = useCallback((value) => {
    setItemSort(value);
    setItemPage(0);
  }, []);

  const handleCloseItems = useCallback(() => {
    setSelectedModule(null);
    setItemsData(null);
  }, []);

  /**
   * The filters every drawer query carries. Built once so the two levels cannot disagree about
   * which events belong to this item — a people list built under one date range and a timeline
   * built under another would show a fan whose events then came back empty.
   */
  const drawerParams = useCallback(
    (module, itemId, offset, limit) => {
      const params = new URLSearchParams();
      params.set('module', module);
      params.set('itemId', itemId);
      params.set('limit', limit);
      params.set('offset', offset);
      if (teamFilter) params.set('teamId', teamFilter._id);
      if (fromDate) params.set('from', new Date(fromDate).toISOString());
      if (toDate) params.set('to', new Date(`${toDate}T23:59:59.999`).toISOString());
      return params;
    },
    [teamFilter, fromDate, toDate]
  );

  // Level 1 of the drawer: who engaged with this item, ranked by total events. `userId` narrows
  // it to one fan's own fresh totals — used to refresh the selected-person header without
  // re-fetching or re-ranking everyone else.
  const fetchEventUsers = useCallback(
    async (module, itemId, offset, limit, userId) => {
      const params = drawerParams(module, itemId, offset, limit);
      if (userId) params.set('userId', userId);

      const res = await Api.get(`/admin/insights/eventUsers?${params.toString()}`, null, {
        silent: true
      });
      return res?.data;
    },
    [drawerParams]
  );

  // Level 2: the raw timeline, narrowed to one fan when `userId` is given.
  const fetchEvents = useCallback(
    async (module, itemId, offset, limit, userId) => {
      const params = drawerParams(module, itemId, offset, limit);
      if (userId) params.set('userId', userId);

      const res = await Api.get(`/admin/insights/events?${params.toString()}`, null, {
        silent: true
      });
      return res?.data;
    },
    [drawerParams]
  );

  const bucketItems = [
    { label: 'Reach', value: fmtNumber(summary?.totals?.reach) },
    { label: 'Activity', value: fmtNumber(summary?.totals?.activity) },
    { label: 'Creation', value: fmtNumber(summary?.totals?.creation) },
    { label: 'Participation', value: fmtNumber(summary?.totals?.participation) },
    { label: 'Insights', value: fmtNumber(summary?.totals?.total) }
  ];

  const fanChatItems = [
    { label: t('admin.insights.fanchatTopics.messages'), value: fmtNumber(fanChat?.messages) },
    { label: t('admin.insights.fanchatTopics.previews'), value: fmtNumber(fanChat?.previews) },
    { label: t('admin.insights.fanchatTopics.joins'), value: fmtNumber(fanChat?.joins) },
    {
      label: t('admin.insights.fanchatTopics.joinRate'),
      value: fmtRate(fanChat?.joinRate),
      hint: t('admin.insights.fanchatTopics.joinRateHint')
    },
    {
      label: t('admin.insights.fanchatTopics.topicsCreated'),
      value: fmtNumber(fanChat?.topicsCreated),
      hint: t('admin.insights.fanchatTopics.topicsCreatedHint')
    },
    {
      label: t('admin.insights.fanchatTopics.topicsPublished'),
      value: fmtNumber(fanChat?.topicsPublished)
    },
    {
      label: t('admin.insights.fanchatTopics.medianFirstResponse'),
      value: fmtDuration(fanChat?.medianFirstResponseSec, t)
    },
    {
      label: t('admin.insights.fanchatTopics.activeNow'),
      value: fmtNumber(fanChat?.activeNow),
      hint: t('admin.insights.fanchatTopics.activeNowHint')
    }
  ];

  // The access events only exist from when this shipped. With none yet there is nothing to report,
  // and a zero would read as "nobody hit a limit" rather than "not measured".
  const accessSince = fanChat?.access?.since ? new Date(fanChat.access.since) : null;
  const accessHint =
    accessSince && fromDate && new Date(fromDate) < accessSince
      ? t('admin.insights.fanchatTopics.access.sinceHint', {
          date: accessSince.toLocaleDateString()
        })
      : undefined;
  const accessValue = (n) =>
    accessSince ? fmtNumber(n) : t('admin.insights.fanchatTopics.access.noData');
  const fanChatAccessItems = [
    {
      label: t('admin.insights.fanchatTopics.access.freeFansUsed'),
      value: accessValue(fanChat?.access?.freeFansUsed),
      hint: accessHint
    },
    {
      label: t('admin.insights.fanchatTopics.access.limitReachedFans'),
      value: accessValue(fanChat?.access?.limitReachedFans)
    },
    {
      label: t('admin.insights.fanchatTopics.access.limitReachedPosts'),
      value: accessValue(fanChat?.access?.limitReachedPosts)
    },
    {
      label: t('admin.insights.fanchatTopics.access.limitReachedReplies'),
      value: accessValue(fanChat?.access?.limitReachedReplies)
    },
    {
      label: t('admin.insights.fanchatTopics.access.paywallLimitShown'),
      value: accessValue(fanChat?.access?.paywallLimitShown)
    },
    {
      label: t('admin.insights.fanchatTopics.access.followPromptShown'),
      value: accessValue(fanChat?.access?.followPromptShown)
    },
    {
      label: t('admin.insights.fanchatTopics.access.followRefused'),
      value: accessValue(fanChat?.access?.followRefused)
    }
  ];

  return (
    <Col noFlex wid="100%" ht="100vh" start>
      <Header>
        <Row center>
          <Text h3>Insights</Text>
        </Row>
      </Header>

      <div className="mt-[54px] w-full">
        <TabControl
          options={TABS}
          selectedIdx={tabIdx}
          handleSelect={(idx) => setSearchParams({ tab: TAB_KEYS[idx] })}
        />
      </div>

      <Col start noFlex wid="100%" ht="100vh" scrollY pad="0 24px">
        <Row start gap="30px" pad="12px 0" noFlex wrap>
          <Col noFlex>
            <Text marg="0 16px 4px" b2 med>
              Filter by Team
            </Text>
            <Row noFlex wid="280px">
              <SearchUsers
                userType="team"
                selected={teamFilter}
                handleSelect={setTeamFilter}
                includeDummy={true}
                noRounded
              />

              {teamFilter && (
                <Row start between wid="100%" center>
                  <Row noFlex center>
                    <Avatar small user={teamFilter} />
                    <Text marg="0 0 0 8px">{teamFilter?.name || teamFilter?.username}</Text>
                  </Row>

                  <Clickable onClick={() => setTeamFilter(null)}>
                    <Icon type="close" dimensions={14} />
                  </Clickable>
                </Row>
              )}
            </Row>
          </Col>

          {view === 'modules' && (
            <Col noFlex>
              <Text marg="0 16px 4px" b2 med>
                Filter by Module
              </Text>
              <select
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
                className={inputClass}
              >
                {MODULE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Col>
          )}

          <Col noFlex>
            <Text marg="0 16px 4px" b2 med>
              From
            </Text>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className={inputClass}
            />
          </Col>

          <Col noFlex>
            <Text marg="0 16px 4px" b2 med>
              To
            </Text>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className={inputClass}
            />
          </Col>
        </Row>

        {view === 'referrals' && (
          <div className="mb-8 w-full">
            <ReferralSection teamId={teamFilter?._id} fromDate={fromDate} toDate={toDate} />
          </div>
        )}

        {view === 'modules' && (
          <Col noFlex wid="100%">
            <ParticipationSummary loading={loading} items={bucketItems} />
          </Col>
        )}

        {/* The AR filters card and, on the all-modules view, GoalRush kicks per day side by
            side; `items-stretch` gives the GoalRush card the AR card's height. */}
        {view === 'modules' && (
          <div className="mt-6 w-full flex flex-wrap items-stretch gap-5">
            {(!moduleFilter || moduleFilter === 'fancam') &&
              (loading || summary?.topEffects?.length > 0) && (
                <div className="w-full max-w-[420px]">
                  <EffectUsageSummary effects={summary?.topEffects} loading={loading} />
                </div>
              )}
            {showGoalRushCard && (
              <div className="w-full max-w-[420px]">
                <GoalRushDailyCard
                  daily={goalRush?.daily}
                  loading={goalRushLoading && !goalRush}
                  onViewMore={() => setGoalRushOpen(true)}
                />
              </div>
            )}
          </div>
        )}

        {showFanChat && (fanChatLoading || fanChat) && (
          <Col noFlex wid="100%" marg="24px 0 0">
            <Text h4>{t('admin.insights.fanchatTopics.title')}</Text>
            <ParticipationSummary loading={fanChatLoading} items={fanChatItems} />
            <Text h4>{t('admin.insights.fanchatTopics.access.title')}</Text>
            <ParticipationSummary loading={fanChatLoading} items={fanChatAccessItems} />
          </Col>
        )}

        {/* Inline GoalRush panel, replaced by the drawer below; kept for switch-back.
        {view === 'modules' &&
          (moduleFilter === 'goalrush' || selectedModule === 'goalrush') &&
          (goalRushLoading || goalRush) && (
          <GoalRushStats data={goalRush} loading={goalRushLoading} />
        )} */}

        {/* "Open GoalRush analytics" button, replaced by the kicks-per-day card's "View more";
            kept for switch-back.
        {view === 'modules' &&
          (moduleFilter === 'goalrush' || selectedModule === 'goalrush') && (
          <div className="w-full mt-4">
            <button
              type="button"
              onClick={() => setGoalRushOpen(true)}
              className="h-9 px-4 inline-flex items-center rounded-lg border border-[#CBD5E1] bg-white text-[13px] font-medium text-[#334155] transition-colors hover:border-[#94A3B8]"
            >
              {t('admin.insights.goalrushStats.open')}
            </button>
          </div>
        )} */}

        {view !== 'modules' ? null : selectedModule ? (
          // Master-detail split once a module is picked — the module list shrinks into a
          // rail alongside its own items instead of stacking a second block underneath.
          <div className="grid grid-cols-[220px_1fr] gap-5 items-start w-full mt-6 mb-8">
            <ModuleBreakdownTable
              rows={summary?.byModule}
              loading={loading}
              onModuleClick={handleModuleClick}
              selectedModule={selectedModule}
              compact
            />
            <ModuleItemsList
              module={selectedModule}
              items={itemsData?.items}
              total={itemsData?.total ?? 0}
              loading={itemsLoading}
              page={itemPage}
              pageSize={ITEM_PAGE_SIZE}
              onPageChange={setItemPage}
              onClose={handleCloseItems}
              onItemClick={setSelectedItem}
              onRefresh={handleRefreshItems}
              refreshing={itemsRefreshing}
              sort={itemSort}
              onSortChange={handleItemSortChange}
            />
          </div>
        ) : (
          <div className="mt-6 mb-8 w-full">
            <ModuleBreakdownTable
              rows={summary?.byModule}
              loading={loading}
              onModuleClick={handleModuleClick}
              selectedModule={selectedModule}
            />
          </div>
        )}
      </Col>

      {goalRushOpen && (
        <GoalRushAnalyticsDrawer
          data={goalRush}
          loading={goalRushLoading}
          refreshing={goalRushRefreshing}
          team={teamFilter}
          fromDate={fromDate}
          toDate={toDate}
          onRefresh={() => loadGoalRush({ quiet: true })}
          onClose={closeGoalRush}
        />
      )}

      {selectedItem && (
        <ItemActivityDrawer
          item={selectedItem}
          module={selectedModule}
          fetchEvents={fetchEvents}
          fetchEventUsers={fetchEventUsers}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </Col>
  );
};

export default Insights;
